/**
 * Tenant detection  -  reads the subdomain from the current hostname.
 *
 * Why this is a separate module: subdomain routing is the foundation of the
 * eventual multi-tenant story (each prospect gets their own subdomain with
 * customized content). Keeping this in one place means future per-tenant
 * config loading (e.g. from a Supabase tenant_config table) plugs in here.
 *
 * Returns null when:
 *   - We're on the bare apex domain (stackbtc.net)
 *   - We're on localhost without a subdomain prefix
 *   - We're on a Vercel preview URL (host ends with vercel.app)
 *
 * Returns the subdomain string (e.g. "rajat") when present.
 */
export function getCurrentSubdomain(): string | null {
  const override = (import.meta.env as Record<string, string | undefined>)['VITE_TENANT_SLUG'];
  if (override) return override;
  if (typeof window === 'undefined') return null; // SSR safety, harmless in Vite
  const host = window.location.hostname;

  // Treat Vercel preview deployments as "no subdomain" so the landing page
  // doesn't show on preview links. We want previews to act like a normal
  // subdomain (full app).
  if (host.endsWith('.vercel.app')) return 'preview';

  // Strip the apex (last two parts). Anything left is the subdomain stack.
  // Examples:
  //   "rajat.stackbtc.net"     -> ["rajat"]            -> "rajat"
  //   "stackbtc.net"           -> []                   -> null
  //   "rajat.localhost"        -> ["rajat"]            -> "rajat"
  //   "localhost"              -> []                   -> null
  const parts = host.split('.');
  if (parts.length <= 2) return null;
  return parts.slice(0, parts.length - 2).join('.');
}

/**
 * True when the user is on the bare apex domain. Used to gate the landing
 * page. Distinct from `getCurrentSubdomain() === null` only in intent  - 
 * keeping it as a named predicate makes the call site read clearly.
 */
export function isApexDomain(): boolean {
  return getCurrentSubdomain() === null;
}

/**
 * True for tenants that use FIRE framing: "Income" tab, "FIRE" tab, "FIRE
 * milestones", carry-trade defaults, etc. Add new slugs here when a tenant
 * adopts the same UX conventions.
 */
export function isFIRETenant(): boolean {
  const sub = getCurrentSubdomain();
  return sub === 'bradlong' || sub === 'trey' || sub === 'private' || sub === 'paul';
}

/**
 * True for tenants that show the debt paydown section in SalaryPlan and the
 * debt framing note in LoopLoan. Superset of isFIRETenant  -  add new slugs
 * here when a tenant's audience has a meaningful debt-payoff narrative.
 */
export function isDebtTenant(): boolean {
  const sub = getCurrentSubdomain();
  return isFIRETenant() || sub === 'tim';
}
